import React, { useState } from "react";
import { motion } from "framer-motion";
import { Brain, Sparkle, Lightning, X } from "@phosphor-icons/react";
import { api } from "../../utils/api";
import { Button } from "../shared/Button";
import { Textarea } from "../shared/Input";
import { Modal } from "../shared/Modal";

export const AsesorTecnicoIA = ({ isOpen, onClose }) => {
  const [pregunta, setPregunta] = useState("");
  const [respuesta, setRespuesta] = useState("");
  const [loading, setLoading] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [contexto, setContexto] = useState("");

  const contextosDisponibles = [
    { value: "", label: "General (sin contexto específico)" },
    { value: "bano", label: "🚿 Reforma de baño" },
    { value: "cocina", label: "🍳 Reforma de cocina" },
    { value: "suelo", label: "🪵 Instalación de suelos" },
    { value: "pintura", label: "🎨 Pintura y acabados" },
    { value: "puertas", label: "🚪 Puertas y ventanas" },
    { value: "electricidad", label: "⚡ Instalación eléctrica" },
    { value: "fontaneria", label: "🔧 Fontanería" },
    { value: "ceramica", label: "🔲 Cerámica y alicatados" },
    { value: "mamparas", label: "🚿 Mamparas de baño" },
    { value: "mantenimiento", label: "🛠️ Mantenimiento" },
  ];

  // PREGUNTAS DINÁMICAS SEGÚN TIPO DE TRABAJO
  const preguntasPorTipo = {
    "": [
      "¿Qué tipo de reforma recomiendas para mi casa?",
      "¿Cuánto cuesta reformar una vivienda completa?",
      "¿Cuánto tiempo tarda una reforma?",
      "¿Qué permisos necesito para reformar?",
    ],
    "bano": [
      "¿Cuánto cuesta reformar un baño completo?",
      "¿Qué tipo de mampara es mejor?",
      "¿Cuánto dura una reforma de baño?",
      "¿Qué cerámica recomiendan para el baño?",
    ],
    "cocina": [
      "¿Qué pintura usar en la cocina?",
      "¿Cuánto cuesta cambiar los muebles de cocina?",
      "¿Qué encimera es más duradera?",
      "¿Necesito cambiar las tuberías al reformar la cocina?",
    ],
    "suelo": [
      "¿Qué suelo es mejor para el salón?",
      "¿Laminado o tarima flotante?",
      "¿Cuánto cuesta instalar parquet?",
      "¿El suelo laminado resiste el agua?",
    ],
    "pintura": [
      "¿Qué pintura es mejor para paredes húmedas?",
      "¿Cuántas capas de pintura son necesarias?",
      "¿Necesito lijar antes de pintar?",
      "¿Pintura plástica o al temple?",
    ],
    "puertas": [
      "¿Qué tipo de puerta es más segura?",
      "¿Puerta blindada o acorazada?",
      "¿Cuánto cuesta cambiar las ventanas?",
      "¿Ventanas de PVC o aluminio?",
    ],
    "electricidad": [
      "¿Necesito cambiar el cuadro eléctrico?",
      "¿Cuántos puntos de luz recomiendas por habitación?",
      "¿Es obligatorio el certificado eléctrico?",
      "¿Cuánto cuesta una instalación eléctrica nueva?",
    ],
    "fontaneria": [
      "¿Cuándo debo cambiar las tuberías?",
      "¿Qué tipo de calentador es más eficiente?",
      "¿Cuánto cuesta cambiar un WC completo?",
      "¿Necesito permisos para cambiar la fontanería?",
    ],
    "ceramica": [
      "¿Qué tamaño de azulejo es mejor?",
      "¿Cerámica o porcelánico?",
      "¿Cuánto material de más debo comprar?",
      "¿Qué junta es mejor para el baño?",
    ],
    "mamparas": [
      "¿Mampara fija o corredera?",
      "¿Qué grosor de cristal necesito?",
      "¿Cómo evitar las manchas de cal?",
      "¿Cuánto cuesta una mampara a medida?",
    ],
    "mantenimiento": [
      "¿Cada cuánto debo pintar mi casa?",
      "¿Cómo mantener el parquet?",
      "¿Qué hacer si hay humedad en las paredes?",
      "¿Cuándo cambiar la silicona del baño?",
    ],
  };

  const preguntasRapidas = preguntasPorTipo[contexto] || preguntasPorTipo[""];

  const consultar = async (preguntaTexto = null) => {
    const texto = preguntaTexto || pregunta;
    if (!texto.trim()) return;

    setLoading(true);
    try {
      const res = await api.post("/asesor-tecnico", { 
        pregunta: texto,
        contexto: contexto 
      });
      setRespuesta(res.respuesta);
      setHistorial([...historial, { pregunta: texto, respuesta: res.respuesta, contexto }]);
      setPregunta("");
    } catch (e) {
      setRespuesta("Error al consultar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🧠 Asesor Técnico IA">
      <div>
        {/* Selector de contexto */}
        <div className="mb-4">
          <label className="text-label mb-2 block" style={{ color: "var(--brand-primary)" }}>
            TIPO DE TRABAJO
          </label>
          <select
            value={contexto}
            onChange={(e) => setContexto(e.target.value)}
            className="w-full p-3 rounded-lg border border-subtle bg-surface-secondary text-small"
            style={{ outline: "none" }}
          >
            {contextosDisponibles.map((ctx) => (
              <option key={ctx.value} value={ctx.value}>
                {ctx.label}
              </option>
            ))}
          </select>
          <div className="text-tiny text-secondary mt-1">
            Selecciona el tipo de reforma para obtener respuestas más precisas
          </div>
        </div>

        {/* Preguntas rápidas */}
        {!respuesta && (
          <div className="mb-4">
            <div className="text-label mb-2" style={{ color: "var(--brand-primary)" }}>PREGUNTAS FRECUENTES</div>
            <div className="space-y-2">
              {preguntasRapidas.map((p, i) => (
                <button
                  key={i}
                  onClick={() => consultar(p)}
                  className="card p-3 w-full text-left card-interactive"
                  style={{ background: "var(--surface-secondary)" }}
                >
                  <div className="text-small font-bold">{p}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input consulta */}
        <Textarea
          label="Tu pregunta"
          placeholder="Ej: ¿Qué tipo de mampara recomiendan?"
          value={pregunta}
          onChange={(e) => setPregunta(e.target.value)}
          rows={3}
        />

        <Button
          variant="accent"
          className="btn-full mb-4"
          onClick={() => consultar()}
          disabled={loading || !pregunta.trim()}
          icon={<Lightning size={20} weight="fill" />}
        >
          {loading ? "Consultando..." : "Consultar Asesor"}
        </Button>

        {/* Respuesta */}
        {respuesta && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card card-dark p-4 mb-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <Brain size={24} weight="fill" style={{ color: "var(--brand-accent)" }} />
              <div className="text-label" style={{ color: "var(--brand-accent)" }}>RESPUESTA</div>
            </div>
            <div className="text-small" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
              {respuesta}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => setRespuesta("")}
            >
              Nueva consulta
            </Button>
          </motion.div>
        )}

        {/* Historial */}
        {historial.length > 0 && !respuesta && (
          <div>
            <div className="text-label mb-2" style={{ color: "var(--brand-primary)" }}>CONSULTAS ANTERIORES</div>
            <div className="space-y-2" style={{ maxHeight: 200, overflowY: "auto" }}>
              {historial.slice(-5).reverse().map((item, i) => (
                <div key={i} className="card p-3" style={{ background: "var(--surface-secondary)" }}>
                  <div className="text-tiny font-bold mb-1">{item.pregunta}</div>
                  <div className="text-tiny text-secondary">{item.respuesta.substring(0, 100)}...</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
